/* Pen3D theme — vanilla JS, no dependencies */
(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------------- Drawers (menu + cart) ---------------- */
  class Drawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      setTimeout(() => $('.drawer__panel', this)?.focus(), 50);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      this.opener?.focus?.();
    }
  }
  customElements.define('menu-drawer', class extends Drawer {});

  class CartDrawer extends Drawer {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-change]');
        if (btn) updateLine(btn.dataset.line, parseInt(btn.dataset.qtyChange, 10));
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-qty-input]');
        if (input) updateLine(input.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
      });
    }
    /** Replace drawer contents with freshly rendered section HTML */
    renderFrom(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('.drawer__panel', doc);
      const panel = $('.drawer__panel', this);
      if (fresh && panel) panel.innerHTML = fresh.innerHTML;
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      document.getElementById(opener.dataset.drawerOpen)?.open(opener);
      return;
    }
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle && theme.cartType === 'drawer' && !document.body.classList.contains('template-cart')) {
      e.preventDefault();
      $('cart-drawer')?.open(cartToggle);
    }
  });

  /* ---------------- Cart API ---------------- */
  const setCount = (count) => {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.dataset.count = count; });
  };

  async function updateLine(line, quantity) {
    const drawer = $('cart-drawer');
    const onCartPage = document.body.classList.contains('template-cart');
    drawer?.classList.add('is-loading');
    try {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (onCartPage) return window.location.reload();
      setCount(data.item_count);
      drawer?.renderFrom(data.sections?.['cart-drawer']);
    } catch (err) {
      console.error(err);
    } finally {
      drawer?.classList.remove('is-loading');
    }
  }

  /* Product forms: add to cart via AJAX, then open the drawer */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const buttons = $$(`[data-add-button]`).filter((b) => b.form === form || form.contains(b));
    const errorEl = $('[data-form-error]', form);
    if (buttons.some((b) => b.disabled)) return;
    buttons.forEach((b) => b.classList.add('is-loading'));
    if (errorEl) errorEl.hidden = true;

    const body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      if (theme.cartType === 'page') { window.location.href = theme.routes.cart; return; }
      const drawer = $('cart-drawer');
      drawer?.renderFrom(data.sections?.['cart-drawer']);
      const cart = await fetch(`${theme.routes.cart}.js`).then((r) => r.json());
      setCount(cart.item_count);
      drawer?.open();
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
    } finally {
      buttons.forEach((b) => b.classList.remove('is-loading'));
    }
  });

  /* ---------------- Quantity input ---------------- */
  customElements.define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      const input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn) return;
        const min = parseInt(input.min, 10) || 1;
        input.value = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(btn.dataset.step, 10));
      });
    }
  });

  /* ---------------- Media gallery ---------------- */
  customElements.define('media-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.thumbs = $$('[data-thumb]', this);
      this.prev = $('[data-gallery-prev]', this);
      this.next = $('[data-gallery-next]', this);
      if (!this.track) return;
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb))));
      this.prev?.addEventListener('click', () => this.go(this.index - 1));
      this.next?.addEventListener('click', () => this.go(this.index + 1));
      this.index = 0;
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.sync());
      }, { passive: true });
      this.sync();
    }
    get count() { return this.track.children.length; }
    go(i) {
      const idx = (i + this.count) % this.count;
      this.track.scrollTo({ left: this.track.clientWidth * idx, behavior: 'smooth' });
    }
    goToMedia(mediaId) {
      const slides = Array.from(this.track.children);
      const i = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.go(i);
    }
    sync() {
      this.index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.index));
      const active = this.thumbs[this.index];
      if (active) active.parentElement.scrollTo({ left: active.offsetLeft - 40, behavior: 'smooth' });
      // pause any video that scrolled away
      $$('video', this.track).forEach((v, i) => { if (i !== this.index) v.pause?.(); });
    }
  });

  /* ---------------- Variant picker ---------------- */
  customElements.define('variant-picker', class extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('[data-variants]', this)?.textContent || '[]');
      this.section = this.closest('[data-section]') || document;
      this.addEventListener('change', () => this.update());
    }
    update() {
      const selected = $$('input[type="radio"]:checked', this).map((i) => i.value);
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      const fieldsets = $$('fieldset', this);
      fieldsets.forEach((fs, i) => { const l = $('[data-option-label]', fs); if (l) l.textContent = selected[i]; });
      const idInput = $('[data-variant-id]', this.section);
      const buttons = $$('[data-add-button]');
      if (!variant) {
        buttons.forEach((b) => { b.disabled = true; const l = $('[data-add-label]', b); if (l) l.textContent = theme.strings.unavailable; });
        return;
      }
      if (idInput) idInput.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        const l = $('[data-add-label]', b);
        if (!l) return;
        if (!l.dataset.original) l.dataset.original = l.textContent.trim();
        l.textContent = variant.available ? l.dataset.original : theme.strings.soldOut;
      });
      const money = (cents) => (cents / 100).toLocaleString(document.documentElement.lang || 'fr', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' });
      $$('[data-price-current]').forEach((el) => { el.textContent = money(variant.price); });
      $$('[data-price-compare]').forEach((el) => {
        el.hidden = !(variant.compare_at_price > variant.price);
        if (!el.hidden) el.textContent = money(variant.compare_at_price);
      });
      if (variant.featured_media) $('media-gallery')?.goToMedia(variant.featured_media.id);
    }
  });

  /* ---------------- Sticky add to cart ---------------- */
  customElements.define('sticky-atc', class extends HTMLElement {
    connectedCallback() {
      const target = $('[data-main-atc]');
      const footer = $('.footer');
      if (!target) return;
      let pastButton = false, atFooter = false;
      const render = () => {
        const show = pastButton && !atFooter;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
      };
      new IntersectionObserver(([entry]) => {
        pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        render();
      }).observe(target);
      if (footer) new IntersectionObserver(([entry]) => { atFooter = entry.isIntersecting; render(); }).observe(footer);
    }
  });

  /* ---------------- Carousel ---------------- */
  customElements.define('carousel-slider', class extends HTMLElement {
    connectedCallback() {
      const track = $('[data-carousel-track]', this);
      const prev = $('[data-carousel-prev]', this);
      const next = $('[data-carousel-next]', this);
      if (!track) return;
      const step = () => (track.firstElementChild?.getBoundingClientRect().width || track.clientWidth) + 14;
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 4;
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max;
        const nav = $('.carousel__nav', this);
        if (nav) nav.hidden = track.scrollWidth <= track.clientWidth + 4;
      };
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    }
  });

  /* ---------------- Before / after ---------------- */
  customElements.define('before-after', class extends HTMLElement {
    connectedCallback() {
      const range = $('.ba__range', this);
      range?.addEventListener('input', () => this.style.setProperty('--pos', `${range.value}%`));
    }
  });

  /* ---------------- Announcement bar ---------------- */
  customElements.define('announcement-bar', class extends HTMLElement {
    connectedCallback() {
      const slides = $$('.announcement__slide', this);
      if (slides.length < 2) return;
      let i = 0;
      setInterval(() => {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, Number(this.dataset.interval) || 4000);
    }
  });

  customElements.define('countdown-timer', class extends HTMLElement {
    connectedCallback() {
      const end = new Date(this.dataset.end).getTime();
      if (Number.isNaN(end)) return;
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const diff = end - Date.now();
        if (diff <= 0) { this.hidden = true; clearInterval(this.timer); return; }
        this.hidden = false;
        const s = Math.floor(diff / 1000);
        $('[data-d]', this).textContent = pad(Math.floor(s / 86400));
        $('[data-h]', this).textContent = pad(Math.floor((s % 86400) / 3600));
        $('[data-m]', this).textContent = pad(Math.floor((s % 3600) / 60));
        $('[data-s]', this).textContent = pad(s % 60);
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
  });

  /* ---------------- Videos ---------------- */
  document.addEventListener('click', (e) => {
    const bubble = e.target.closest('[data-video-open]');
    if (bubble) {
      const modal = $('[data-video-modal]');
      const video = $('video', modal);
      video.src = bubble.dataset.videoOpen;
      modal.showModal();
      video.play().catch(() => {});
      return;
    }
    if (e.target.closest('[data-video-close]') || e.target.matches('[data-video-modal]')) {
      const modal = $('[data-video-modal]');
      $('video', modal).pause();
      modal.close();
      return;
    }
    const card = e.target.closest('[data-video-card]');
    if (card) {
      const v = $('video', card);
      $$('[data-video-card] video').forEach((o) => { if (o !== v) { o.pause(); o.closest('[data-video-card]').classList.remove('is-playing'); } });
      if (v.paused) { v.muted = false; v.play(); card.classList.add('is-playing'); } else { v.pause(); card.classList.remove('is-playing'); }
    }
  });

  /* ---------------- Header state + reveal on scroll ---------------- */
  const header = $('[data-header]');
  if (header) {
    const setH = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
    setH();
    window.addEventListener('resize', setH);
    window.addEventListener('scroll', () => header.classList.toggle('is-scrolled', window.scrollY > 8), { passive: true });
  }

  if (!document.body.classList.contains('no-anim') && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    $$('.reveal').forEach((el) => io.observe(el));
  } else {
    $$('.reveal').forEach((el) => el.classList.add('is-in'));
  }

  // Theme editor: re-run reveals for sections added/changed live
  document.addEventListener('shopify:section:load', (e) => $$('.reveal', e.target).forEach((el) => el.classList.add('is-in')));
})();
